/* Pivot: collection filters. Opens/closes the filter dropdown, drives the era timeline,
   refreshes the product grid in place (Shopify Section Rendering API) when anything changes,
   and loads the next page of products as the visitor scrolls (endless scroll). */
if (!customElements.get('pivot-collection')) {
  customElements.define(
    'pivot-collection',
    class PivotCollection extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.form = this.querySelector('[data-pivot-filters]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (!this.form) return this.observeNextPage(); // Filters switched off (Archive): endless scroll only
        this.panel = this.querySelector('[data-pivot-filter-panel]');
        this.toggleButton = this.querySelector('[data-pivot-filter-toggle]');

        this.toggleButton.addEventListener('click', () => this.setOpen(!this.form.classList.contains('is-open')));
        this.form.addEventListener('submit', (event) => event.preventDefault());
        this.form.addEventListener('change', (event) => {
          if (event.target.closest('[data-pivot-era]')) return;
          this.update();
        });
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-pivot-clear]');
          if (!link) return;
          event.preventDefault();
          this.clearFilters();
          this.update(new URLSearchParams(new FormData(this.form)));
        });
        // Back/forward between filtered states. Ignores hash-only changes (e.g. the skip-to-content link).
        this.lastSearch = window.location.search;
        this.onPopState = () => {
          if (window.location.search === this.lastSearch) return;
          const params = new URLSearchParams(window.location.search);
          this.setEraFromParams(params);
          this.update(params, false);
        };
        window.addEventListener('popstate', this.onPopState);

        this.initEra();
        this.observeNextPage();
      }

      // The theme editor re-renders sections; don't leave a listener behind
      disconnectedCallback() {
        if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
        this.observer?.disconnect();
        this.nextController?.abort();
        this.updateController?.abort();
      }

      /* ---------- Endless scroll ---------- */

      observeNextPage() {
        this.observer?.disconnect();
        const trigger = this.querySelector('[data-pivot-next]');
        if (!trigger || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage(trigger);
          },
          { rootMargin: '0px 0px 800px 0px' }
        );
        this.observer.observe(trigger);
      }

      async loadNextPage(trigger) {
        if (this.loadingMore) return;
        this.loadingMore = true;
        this.observer?.disconnect();
        this.nextController = new AbortController();
        try {
          const url = new URL(trigger.href, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url, { signal: this.nextController.signal });
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          // Filters changed while this page was loading: its products belong to the old results
          if (!trigger.isConnected) return;
          const list = this.querySelector('[data-pivot-results] .pivot-product-grid__list');
          html.querySelectorAll('[data-pivot-results] .pivot-product-grid__list > li').forEach((item) => list.append(item));
          const next = html.querySelector('[data-pivot-next]');
          if (next) trigger.replaceWith(next);
          else trigger.remove();
        } catch (error) {
          return; // the trigger stays a normal link to the next page
        } finally {
          this.loadingMore = false;
        }
        this.observeNextPage();
      }

      /* ---------- Dropdown ---------- */

      setOpen(open) {
        const from = this.panel.getBoundingClientRect().height;
        this.animation?.cancel();
        this.toggleButton.setAttribute('aria-expanded', open);
        if (open) this.form.classList.add('is-open');

        if (this.reduceMotion.matches) {
          if (!open) this.form.classList.remove('is-open');
          return;
        }

        const to = open ? this.panel.scrollHeight : 0;
        this.animation = this.panel.animate(
          { height: [`${open ? 0 : from}px`, `${to}px`] },
          { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
        );
        this.animation.onfinish = () => {
          this.animation = null;
          if (!open) this.form.classList.remove('is-open');
        };
      }

      /* ---------- Era timeline ----------
         Two handles that move freely and can pass each other: the range is always from the lower
         handle to the higher one. The chosen range is kept in the URL (?era=1-4) so it survives
         reloads and back/forward, even when no piece falls inside it. */

      initEra() {
        this.era = this.querySelector('[data-pivot-era]');
        if (!this.era) return;
        this.eraLo = this.era.querySelector('[data-pivot-era-lo]');
        this.eraHi = this.era.querySelector('[data-pivot-era-hi]');
        this.eraLoDot = this.era.querySelector('[data-pivot-era-lo-dot]');
        this.eraHiDot = this.era.querySelector('[data-pivot-era-hi-dot]');
        this.long = this.era.dataset.long.split(',');
        this.short = this.era.dataset.short.split(',');

        const onInput = (event) => {
          // Keep the handle being dragged on top, so it can always move off the other one
          this.eraLo.style.zIndex = event.target === this.eraLo ? 2 : 1;
          this.eraHi.style.zIndex = event.target === this.eraHi ? 2 : 1;
          this.renderEra();
        };
        this.eraLo.addEventListener('input', onInput);
        this.eraHi.addEventListener('input', onInput);
        this.eraLo.addEventListener('change', () => this.applyEra());
        this.eraHi.addEventListener('change', () => this.applyEra());
        this.initEraDrag();

        if (this.setEraFromParams(new URLSearchParams(window.location.search))) this.showEraEmpty();
        this.renderEra();
      }

      /* Touch and mouse: press anywhere on the timeline and drag. The nearer dot follows your finger;
         if both dots sit together, the first sideways move decides which one comes away. */
      initEraDrag() {
        const track = this.era.querySelector('.pivot-era__track');
        if (!track || this.eraLo.disabled) return;
        const stepAt = (x) => {
          const line = this.era.querySelector('.pivot-era__line').getBoundingClientRect();
          const ratio = Math.min(Math.max((x - line.left) / line.width, 0), 1);
          return Math.round(ratio * 6);
        };
        const set = (input, value) => {
          if (Number(input.value) === value) return;
          input.value = value;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          this.eraDragMoved = true;
        };

        track.addEventListener('pointerdown', (event) => {
          if (event.button > 0) return;
          const value = stepAt(event.clientX);
          const lo = Number(this.eraLo.value);
          const hi = Number(this.eraHi.value);
          this.eraDragMoved = false;
          this.eraDragStartX = event.clientX;
          if (lo === hi) {
            this.eraDragInput = null; // decided on the first move
            if (value !== lo) {
              this.eraDragInput = value < lo ? this.eraLo : this.eraHi;
              set(this.eraDragInput, value);
            }
          } else {
            this.eraDragInput = Math.abs(value - lo) <= Math.abs(value - hi) ? this.eraLo : this.eraHi;
            set(this.eraDragInput, value);
          }
          this.era.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        });

        track.addEventListener('pointermove', (event) => {
          if (!this.era.classList.contains('is-dragging')) return;
          const value = stepAt(event.clientX);
          if (!this.eraDragInput) {
            if (value === Number(this.eraLo.value)) return;
            this.eraDragInput = event.clientX < this.eraDragStartX ? this.eraLo : this.eraHi;
          }
          set(this.eraDragInput, value);
        });

        const end = () => {
          if (!this.era.classList.contains('is-dragging')) return;
          this.era.classList.remove('is-dragging');
          if (this.eraDragMoved) this.applyEra();
        };
        track.addEventListener('pointerup', end);
        track.addEventListener('pointercancel', end);
      }

      /* The range as [lower, higher], whichever handle is where */
      eraRange() {
        const a = Number(this.eraLo.value);
        const b = Number(this.eraHi.value);
        return [Math.min(a, b), Math.max(a, b)];
      }

      eraIsFull() {
        const [lo, hi] = this.eraRange();
        return lo === 0 && hi === 6;
      }

      /* ?era=lo-hi from the URL; returns true if one was found */
      setEraFromParams(params) {
        if (!this.era) return false;
        const match = /^([0-6])-([0-6])$/.exec(params.get('era') || '');
        if (!match) return false;
        this.eraLo.value = match[1];
        this.eraHi.value = match[2];
        this.renderEra();
        return true;
      }

      renderEra() {
        const [lo, hi] = this.eraRange();
        const set = (el, i) => {
          el.querySelector('.pivot-era__long').textContent = this.long[i];
          el.querySelector('.pivot-era__short').textContent = this.short[i];
        };
        set(this.era.querySelector('[data-pivot-era-lo-label]'), lo);
        set(this.era.querySelector('[data-pivot-era-hi-label]'), hi);
        this.eraLoDot.style.setProperty('--pivot-era-pos', `${(Number(this.eraLo.value) / 6) * 100}%`);
        this.eraHiDot.style.setProperty('--pivot-era-pos', `${(Number(this.eraHi.value) / 6) * 100}%`);
      }

      /* Checks every decade between the handles; the full 40s-00s span means no era filter */
      applyEra() {
        const [lo, hi] = this.eraRange();
        const full = this.eraIsFull();
        this.era.querySelectorAll('[data-pivot-decade]').forEach((input) => {
          const decade = Number(input.dataset.pivotDecade);
          input.checked = !full && decade >= lo && decade <= hi;
        });
        this.update();
      }

      /* A range with no decades that have pieces: Shopify would ignore the empty filter and show
         everything, so show the "no matches" message instead */
      eraHasNoMatches() {
        if (!this.era || this.eraIsFull()) return false;
        const [lo, hi] = this.eraRange();
        return ![...this.era.querySelectorAll('[data-pivot-decade]')].some((input) => {
          const decade = Number(input.dataset.pivotDecade);
          return decade >= lo && decade <= hi;
        });
      }

      showEraEmpty() {
        if (!this.eraHasNoMatches()) return false;
        const template = this.era.querySelector('[data-pivot-era-empty]');
        const results = this.querySelector('[data-pivot-results]');
        if (!template || !results) return false;
        results.replaceChildren(template.content.cloneNode(true));
        document.querySelectorAll('[data-pivot-swap="search-count"]').forEach((el) => (el.textContent = '(0)'));
        return true;
      }

      clearFilters() {
        this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => (input.checked = false));
        if (this.eraLo) {
          this.eraLo.value = 0;
          this.eraHi.value = 6;
          this.renderEra();
        }
      }

      /* ---------- Results ---------- */

      async update(params = null, pushState = true) {
        if (!params) {
          params = new URLSearchParams(new FormData(this.form));
          params.delete('page');
          if (this.era && !this.eraIsFull()) {
            const [lo, hi] = this.eraRange();
            params.set('era', `${lo}-${hi}`);
          }
        }
        const query = params.toString();
        this.classList.add('is-loading');
        // Only the latest change counts: cancel earlier requests and any next page still loading
        this.updateController?.abort();
        this.nextController?.abort();
        const controller = (this.updateController = new AbortController());

        try {
          const url = `${this.form.action}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
          const response = await fetch(url, { signal: controller.signal });
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const swap = (selector) => {
            const next = html.querySelector(selector);
            const current = this.querySelector(selector);
            if (next && current) current.innerHTML = next.innerHTML;
          };
          swap('[data-pivot-results]');
          swap('[data-pivot-filter-groups]');
          swap('[data-pivot-filter-clear]');
          swap('[data-pivot-era-inputs]');
          html.querySelectorAll('[data-pivot-swap]').forEach((next) => {
            const current = document.querySelector(`[data-pivot-swap="${next.dataset.pivotSwap}"]`);
            if (current) current.innerHTML = next.innerHTML;
          });

          // Keep the range the visitor chose; only take the page's range when the URL doesn't carry one
          const nextEra = html.querySelector('[data-pivot-era]');
          if (nextEra && this.eraLo && !params.has('era')) {
            this.eraLo.value = nextEra.dataset.lo;
            this.eraHi.value = nextEra.dataset.hi;
            this.renderEra();
          }
          this.showEraEmpty();

          if (pushState) history.pushState(null, '', `${this.form.action}${query ? `?${query}` : ''}`);
          this.lastSearch = window.location.search;
          this.observeNextPage();
        } catch (error) {
          if (error.name === 'AbortError') return;
          window.location.assign(`${this.form.action}${query ? `?${query}` : ''}`);
        } finally {
          if (this.updateController === controller) this.classList.remove('is-loading');
        }
      }
    }
  );
}
